import React, { useEffect, useState, useMemo } from 'react';
import { api } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Plus, Search, ExternalLink, FileText, Edit, Trash2, Filter, ArrowUpDown, Play, CheckCircle2, PauseCircle, Archive, LayoutGrid } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const Processos = () => {
  const navigate = useNavigate();
  const [processos, setProcessos] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('todos');
  const [ordenacao, setOrdenacao] = useState('recente');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingProcesso, setEditingProcesso] = useState(null);
  const [formData, setFormData] = useState({
    cliente_id: '',
    numero_processo: '',
    tipo_acao: '',
    status: 'Em andamento',
    descricao: '',
    valor_causa: '',
    data_distribuicao: '',
    data_limite: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [processosRes, clientesRes] = await Promise.all([
        api.getProcessos(),
        api.getClientes()
      ]);
      setProcessos(processosRes.data);
      setClientes(clientesRes.data);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...formData,
        valor_causa: formData.valor_causa ? parseFloat(formData.valor_causa) : null,
        data_distribuicao: formData.data_distribuicao || null,
        data_limite: formData.data_limite || null
      };
      
      if (editingProcesso) {
        await api.updateProcesso(editingProcesso.id, submitData);
        toast.success('Processo atualizado com sucesso!');
      } else {
        await api.createProcesso(submitData);
        toast.success('Processo cadastrado com sucesso!');
      }
      
      setIsDialogOpen(false);
      setEditingProcesso(null);
      setFormData({
        cliente_id: '',
        numero_processo: '',
        tipo_acao: '',
        status: 'Em andamento',
        descricao: '',
        valor_causa: '',
        data_distribuicao: '',
        data_limite: ''
      });
      loadData();
    } catch (error) {
      console.error('Erro ao salvar processo:', error);
      toast.error('Erro ao salvar processo');
    }
  };

  const handleEdit = (processo) => {
    setEditingProcesso(processo);
    setFormData({
      cliente_id: processo.cliente_id,
      numero_processo: processo.numero_processo,
      tipo_acao: processo.tipo_acao,
      status: processo.status,
      descricao: processo.descricao || '',
      valor_causa: processo.valor_causa || '',
      data_distribuicao: processo.data_distribuicao ? processo.data_distribuicao.split('T')[0] : '',
      data_limite: processo.data_limite ? processo.data_limite.split('T')[0] : ''
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (processoId) => {
    if (!window.confirm('Deseja realmente excluir este processo?')) return;

    try {
      await api.deleteProcesso(processoId);
      toast.success('Processo excluído com sucesso!');
      loadData();
    } catch (error) {
      console.error('Erro ao excluir processo:', error);
      toast.error('Erro ao excluir processo');
    }
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingProcesso(null);
    setFormData({
      cliente_id: '',
      numero_processo: '',
      tipo_acao: '',
      status: 'Em andamento',
      descricao: '',
      valor_causa: '',
      data_distribuicao: '',
      data_limite: ''
    });
  };

  const handleConsultarTribunal = async (processoId) => {
    try {
      const response = await api.getTribunalInfo(processoId);
      const { url, tribunal, sistema } = response.data;
      if (url) {
        window.open(url, '_blank');
        toast.success(`Abrindo ${tribunal} - ${sistema}`);
      } else {
        toast.error('Tribunal não identificado. Verifique o número do processo.');
      }
    } catch (error) {
      console.error('Erro ao consultar tribunal:', error);
      toast.error('Erro ao consultar tribunal');
    }
  };

  const filteredProcessos = useMemo(() => {
    let result = processos.filter(processo => {
      const matchesSearch = processo.numero_processo.toLowerCase().includes(searchTerm.toLowerCase()) ||
        processo.cliente?.nome_completo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        processo.tipo_acao.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesStatus = statusFilter === 'todos' || processo.status === statusFilter;
      
      return matchesSearch && matchesStatus;
    });

    // Ordenação
    switch (ordenacao) {
      case 'nome_asc':
        result.sort((a, b) => (a.cliente?.nome_completo || '').localeCompare(b.cliente?.nome_completo || '', 'pt-BR'));
        break;
      case 'nome_desc':
        result.sort((a, b) => (b.cliente?.nome_completo || '').localeCompare(a.cliente?.nome_completo || '', 'pt-BR'));
        break;
      case 'antigo':
        result.sort((a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0));
        break;
      case 'recente':
      default:
        result.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
        break;
    }

    return result;
  }, [processos, searchTerm, statusFilter, ordenacao]);

  // Estatísticas por status
  const estatisticas = useMemo(() => {
    return {
      total: processos.length,
      emAndamento: processos.filter(p => p.status === 'Em andamento').length,
      encerrado: processos.filter(p => p.status === 'Encerrado').length,
      suspenso: processos.filter(p => p.status === 'Suspenso').length,
      arquivado: processos.filter(p => p.status === 'Arquivado').length
    };
  }, [processos]);

  const getStatusColor = (status) => {
    const colors = {
      'Em andamento': 'bg-blue-100 text-blue-700',
      'Encerrado': 'bg-green-100 text-green-700',
      'Suspenso': 'bg-amber-100 text-amber-700',
      'Arquivado': 'bg-slate-100 text-slate-700'
    };
    return colors[status] || 'bg-slate-100 text-slate-700';
  };

  if (loading) {
    return <div className="p-8" data-testid="processos-loading">Carregando...</div>;
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6" data-testid="processos-page">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold text-slate-900">Processos</h1>
          <p className="text-slate-600 mt-1 text-sm sm:text-base">Gerencie seus processos jurídicos</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-slate-900 hover:bg-slate-800 w-full sm:w-auto" data-testid="add-processo-button">
              <Plus className="w-4 h-4 mr-2" />
              Novo Processo
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingProcesso ? 'Editar Processo' : 'Cadastrar Novo Processo'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-4">
                <div>
                  <Label htmlFor="cliente_id">Cliente *</Label>
                  <Select
                    value={formData.cliente_id}
                    onValueChange={(value) => setFormData({ ...formData, cliente_id: value })}
                    required
                  >
                    <SelectTrigger data-testid="processo-cliente-select">
                      <SelectValue placeholder="Selecione um cliente" />
                    </SelectTrigger>
                    <SelectContent>
                      {clientes.map((cliente) => (
                        <SelectItem key={cliente.id} value={cliente.id}>
                          {cliente.nome_completo}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="numero_processo">Número do Processo (CNJ) *</Label>
                  <Input
                    id="numero_processo"
                    data-testid="processo-numero-input"
                    required
                    placeholder="0000000-00.0000.0.00.0000"
                    value={formData.numero_processo}
                    onChange={(e) => setFormData({ ...formData, numero_processo: e.target.value })}
                  />
                  <p className="text-xs text-slate-500 mt-1">O tribunal será identificado automaticamente</p>
                </div>
                <div>
                  <Label htmlFor="tipo_acao">Tipo de Ação *</Label>
                  <Input
                    id="tipo_acao"
                    data-testid="processo-tipo-input"
                    required
                    placeholder="Ex: Ação de Cobrança, Reclamação Trabalhista"
                    value={formData.tipo_acao}
                    onChange={(e) => setFormData({ ...formData, tipo_acao: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="status">Status</Label>
                  <Select
                    value={formData.status}
                    onValueChange={(value) => setFormData({ ...formData, status: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Em andamento">Em andamento</SelectItem>
                      <SelectItem value="Encerrado">Encerrado</SelectItem>
                      <SelectItem value="Suspenso">Suspenso</SelectItem>
                      <SelectItem value="Arquivado">Arquivado</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="valor_causa">Valor da Causa</Label>
                    <Input
                      id="valor_causa"
                      type="number"
                      step="0.01"
                      placeholder="0.00"
                      value={formData.valor_causa}
                      onChange={(e) => setFormData({ ...formData, valor_causa: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="data_distribuicao">Data de Distribuição</Label>
                    <Input
                      id="data_distribuicao"
                      type="date"
                      value={formData.data_distribuicao}
                      onChange={(e) => setFormData({ ...formData, data_distribuicao: e.target.value })}
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="data_limite">Data Limite para Entrada ⚠️</Label>
                  <Input
                    id="data_limite"
                    type="date"
                    value={formData.data_limite}
                    onChange={(e) => setFormData({ ...formData, data_limite: e.target.value })}
                    data-testid="processo-data-limite-input"
                  />
                  <p className="text-xs text-amber-600 mt-1">
                    ⏰ Você receberá um alerta no dashboard com 10 dias de antecedência
                  </p>
                </div>
                <div>
                  <Label htmlFor="descricao">Descrição do Caso</Label>
                  <textarea
                    id="descricao"
                    className="w-full min-h-[100px] p-2 border rounded-md"
                    placeholder="Descreva os detalhes do processo..."
                    value={formData.descricao}
                    onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={handleCloseDialog}>
                  Cancelar
                </Button>
                <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="save-processo-button">
                  {editingProcesso ? 'Atualizar' : 'Cadastrar'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Mini Dashboard de Estatísticas */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        <button
          onClick={() => setStatusFilter('todos')}
          className={`p-4 rounded-xl border-2 transition-all hover:shadow-md ${
            statusFilter === 'todos' 
              ? 'border-slate-900 bg-slate-900 text-white' 
              : 'border-slate-200 bg-white hover:border-slate-300'
          }`}
          data-testid="stat-total"
        >
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${statusFilter === 'todos' ? 'bg-slate-700' : 'bg-slate-100'}`}>
              <LayoutGrid className={`w-5 h-5 ${statusFilter === 'todos' ? 'text-white' : 'text-slate-600'}`} />
            </div>
            <div className="text-left">
              <p className={`text-2xl font-bold ${statusFilter === 'todos' ? 'text-white' : 'text-slate-900'}`}>
                {estatisticas.total}
              </p>
              <p className={`text-xs ${statusFilter === 'todos' ? 'text-slate-300' : 'text-slate-500'}`}>
                Total
              </p>
            </div>
          </div>
        </button>

        <button
          onClick={() => setStatusFilter('Em andamento')}
          className={`p-4 rounded-xl border-2 transition-all hover:shadow-md ${
            statusFilter === 'Em andamento' 
              ? 'border-blue-600 bg-blue-600 text-white' 
              : 'border-blue-200 bg-blue-50 hover:border-blue-300'
          }`}
          data-testid="stat-em-andamento"
        >
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${statusFilter === 'Em andamento' ? 'bg-blue-500' : 'bg-blue-100'}`}>
              <Play className={`w-5 h-5 ${statusFilter === 'Em andamento' ? 'text-white' : 'text-blue-600'}`} />
            </div>
            <div className="text-left">
              <p className={`text-2xl font-bold ${statusFilter === 'Em andamento' ? 'text-white' : 'text-blue-700'}`}>
                {estatisticas.emAndamento}
              </p>
              <p className={`text-xs ${statusFilter === 'Em andamento' ? 'text-blue-200' : 'text-blue-600'}`}>
                Em andamento
              </p>
            </div>
          </div>
        </button>

        <button
          onClick={() => setStatusFilter('Encerrado')}
          className={`p-4 rounded-xl border-2 transition-all hover:shadow-md ${
            statusFilter === 'Encerrado' 
              ? 'border-green-600 bg-green-600 text-white' 
              : 'border-green-200 bg-green-50 hover:border-green-300'
          }`}
          data-testid="stat-encerrado"
        >
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${statusFilter === 'Encerrado' ? 'bg-green-500' : 'bg-green-100'}`}>
              <CheckCircle2 className={`w-5 h-5 ${statusFilter === 'Encerrado' ? 'text-white' : 'text-green-600'}`} />
            </div>
            <div className="text-left">
              <p className={`text-2xl font-bold ${statusFilter === 'Encerrado' ? 'text-white' : 'text-green-700'}`}>
                {estatisticas.encerrado}
              </p>
              <p className={`text-xs ${statusFilter === 'Encerrado' ? 'text-green-200' : 'text-green-600'}`}>
                Encerrados
              </p>
            </div>
          </div>
        </button>

        <button
          onClick={() => setStatusFilter('Suspenso')}
          className={`p-4 rounded-xl border-2 transition-all hover:shadow-md ${
            statusFilter === 'Suspenso' 
              ? 'border-amber-600 bg-amber-600 text-white' 
              : 'border-amber-200 bg-amber-50 hover:border-amber-300'
          }`}
          data-testid="stat-suspenso"
        >
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${statusFilter === 'Suspenso' ? 'bg-amber-500' : 'bg-amber-100'}`}>
              <PauseCircle className={`w-5 h-5 ${statusFilter === 'Suspenso' ? 'text-white' : 'text-amber-600'}`} />
            </div>
            <div className="text-left">
              <p className={`text-2xl font-bold ${statusFilter === 'Suspenso' ? 'text-white' : 'text-amber-700'}`}>
                {estatisticas.suspenso}
              </p>
              <p className={`text-xs ${statusFilter === 'Suspenso' ? 'text-amber-200' : 'text-amber-600'}`}>
                Suspensos
              </p>
            </div>
          </div>
        </button>

        <button
          onClick={() => setStatusFilter('Arquivado')}
          className={`p-4 rounded-xl border-2 transition-all hover:shadow-md col-span-2 sm:col-span-1 ${
            statusFilter === 'Arquivado' 
              ? 'border-slate-600 bg-slate-600 text-white' 
              : 'border-slate-200 bg-slate-50 hover:border-slate-300'
          }`}
          data-testid="stat-arquivado"
        >
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${statusFilter === 'Arquivado' ? 'bg-slate-500' : 'bg-slate-200'}`}>
              <Archive className={`w-5 h-5 ${statusFilter === 'Arquivado' ? 'text-white' : 'text-slate-600'}`} />
            </div>
            <div className="text-left">
              <p className={`text-2xl font-bold ${statusFilter === 'Arquivado' ? 'text-white' : 'text-slate-700'}`}>
                {estatisticas.arquivado}
              </p>
              <p className={`text-xs ${statusFilter === 'Arquivado' ? 'text-slate-300' : 'text-slate-500'}`}>
                Arquivados
              </p>
            </div>
          </div>
        </button>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-3 w-5 h-5 text-slate-400" />
          <Input
            placeholder="Buscar processos por número, cliente ou tipo de ação..."
            className="pl-10"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            data-testid="search-processos-input"
          />
        </div>
        <div className="w-full sm:w-48">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="status-filter-select">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Filtrar por status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todos os status</SelectItem>
              <SelectItem value="Em andamento">Em andamento</SelectItem>
              <SelectItem value="Encerrado">Encerrado</SelectItem>
              <SelectItem value="Suspenso">Suspenso</SelectItem>
              <SelectItem value="Arquivado">Arquivado</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="w-full sm:w-48">
          <Select value={ordenacao} onValueChange={setOrdenacao}>
            <SelectTrigger data-testid="ordenacao-processos-select">
              <ArrowUpDown className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Ordenar por" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="recente">Mais recente</SelectItem>
              <SelectItem value="antigo">Mais antigo</SelectItem>
              <SelectItem value="nome_asc">Cliente (A-Z)</SelectItem>
              <SelectItem value="nome_desc">Cliente (Z-A)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-4">
        {filteredProcessos.length > 0 ? (
          filteredProcessos.map((processo) => (
            <Card key={processo.id} className="hover:shadow-lg transition-shadow" data-testid="processo-card">
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <FileText className="w-5 h-5 text-slate-600" />
                      <CardTitle className="text-lg">{processo.numero_processo}</CardTitle>
                      <span className={`text-xs px-2 py-1 rounded ${getStatusColor(processo.status)}`}>
                        {processo.status}
                      </span>
                    </div>
                    <p className="text-slate-600 mt-2">{processo.tipo_acao}</p>
                    <p className="text-sm text-slate-500 mt-1">
                      Cliente: {processo.cliente?.nome_completo}
                    </p>
                    {processo.tribunal && (
                      <p className="text-sm text-slate-500 mt-1">
                        {processo.tribunal} - {processo.sistema_tribunal}
                      </p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleConsultarTribunal(processo.id)}
                      data-testid="consultar-tribunal-button"
                    >
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Consultar Andamento
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/processos/${processo.id}`)}
                      data-testid="view-processo-button"
                    >
                      Ver Detalhes
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEdit(processo)}
                      data-testid="edit-processo-button"
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDelete(processo.id)}
                      data-testid="delete-processo-button"
                    >
                      <Trash2 className="w-4 h-4 text-red-600" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              {processo.descricao && (
                <CardContent>
                  <p className="text-sm text-slate-600 line-clamp-2">{processo.descricao}</p>
                </CardContent>
              )}
            </Card>
          ))
        ) : (
          <div className="text-center py-12 text-slate-500">
            Nenhum processo encontrado
          </div>
        )}
      </div>
    </div>
  );
};

export default Processos;